/**
 * What the check-in page reads off a guest, an event and a failed request
 * (LIM-4707): who a scan or a search names, what the guest card offers, what
 * staff are told after a tap, and the header's status and tally.
 *
 * The copy is the plan's "What staff see" table, written for someone at a
 * bar holding a phone. Refusals are read by their taxonomy reason
 * (`parseError(e).code`), never their message.
 *
 * Times are shown in this device's timezone (the LIM-1402 rule for display):
 * staff at the door are in the venue's zone, so the venue's own clock is
 * what they read.
 */

import { parseError } from '@liminal/web-ui/errors';
import type { LiminalErrorReason } from '@liminal/errors/liminal-errors';
import { isValidEmail } from '@liminal/utils/validation';
import { EventGuest_Eligibility } from '@/api/generated';
import type { CheckInEvent, EventGuest, EventGuestLookup } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import { parseProfileLink } from '@/lib/profileLink';

// -- Who a scan or a search names --

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ResolveEventGuestRequest.tag's own pattern, with the "@" read off. */
const TAG = /^@?([A-Za-z0-9_.-]+)$/;

/**
 * The guest a scanned code names: a Liminal profile QR is their tag. Anything
 * else is not a guest, and null.
 */
export function lookupFromScan(payload: string): EventGuestLookup | null {
  const link = parseProfileLink(payload);
  return link === null ? null : { by: 'tag', value: link.tag };
}

/**
 * The guest the search field names: a user id, an email address or an @tag
 * (the "@" is optional), or a pasted profile link. Null when it is none of
 * them.
 */
export function lookupFromSearch(text: string): EventGuestLookup | null {
  const value = text.trim();
  const fromLink = lookupFromScan(value);
  if (fromLink !== null) return fromLink;
  if (UUID.test(value)) return { by: 'userId', value: value.toLowerCase() };
  if (isValidEmail(value)) return { by: 'email', value };
  const tag = TAG.exec(value);
  if (tag !== null) return { by: 'tag', value: tag[1] };
  return null;
}

export const SEARCH_HINT = 'Enter an email address, an @tag or a user id.';
export const NOT_A_PROFILE_QR = 'Not a Liminal profile QR.';

// -- Times --

const CLOCK_ONLY_WITHIN_MS = 12 * 60 * 60 * 1000;

function parseTime(iso: string): number {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) throw new Error(`${iso} is not a time`);
  return time;
}

/**
 * A moment as staff read it, in this device's timezone: the clock alone
 * ("21:14") within twelve hours of now, else with its day ("31 Oct, 21:14"),
 * and with its year too when that is not this year.
 */
export function momentText(iso: string, now: number): string {
  const time = parseTime(iso);
  const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(time);
  if (Math.abs(time - now) < CLOCK_ONLY_WITHIN_MS) return clock;
  const year = new Intl.DateTimeFormat('en-GB', { year: 'numeric' });
  const sameYear = year.format(time) === year.format(now);
  const day = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(time);
  return `${day}, ${clock}`;
}

// -- The header --

/**
 * Whether check-in is open, read from the event's window against this
 * device's clock so it turns at the hour without a reload. The server
 * decides at every tap; this is only what the header says.
 */
export function eventStatus(event: CheckInEvent, now: number): { readonly label: string; readonly open: boolean } {
  if (now < parseTime(event.startTime)) return { label: `Opens ${momentText(event.startTime, now)}`, open: false };
  if (now < parseTime(event.endTime)) return { label: `Open until ${momentText(event.endTime, now)}`, open: true };
  return { label: 'Closed', open: false };
}

function counted(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function ticketsText(count: number): string {
  return counted(count, 'ticket', 'tickets');
}

/** "37 guests · 74 tickets". */
export function tallyText(guests: number, tickets: number): string {
  return `${counted(guests, 'guest', 'guests')} · ${ticketsText(tickets)}`;
}

// -- The guest card --

/**
 * Who handed the tickets over, as the card says it: the operator's email up
 * to its "@" ("faiz@"), so the line fits a phone.
 */
export function operatorText(actor: string): string {
  const at = actor.indexOf('@');
  return at === -1 ? actor : actor.slice(0, at + 1);
}

export type NoticeTone = 'warning' | 'error';

export interface Notice {
  readonly tone: NoticeTone;
  readonly text: string;
}

export const NOT_SUBMITTED_TEXT = 'ID verification not submitted. Ask them to finish it and come back.';
export const REJECTED_TEXT = 'Verification was rejected.';
export const FULL_TEXT = 'Ticket allocation used up.';
export const TAP_AGAIN_TEXT = 'Could not reach the server. Tap again.';

/**
 * What the door tells staff about a guest who may not be handed tickets, by
 * their eligibility. Null for the two who may.
 */
const ELIGIBILITY_REFUSALS: Record<EventGuest_Eligibility, Notice | null> = {
  [EventGuest_Eligibility.ELIGIBILITY_SUBMITTED]: null,
  [EventGuest_Eligibility.ELIGIBILITY_VERIFIED]: null,
  [EventGuest_Eligibility.ELIGIBILITY_NOT_SUBMITTED]: { tone: 'warning', text: NOT_SUBMITTED_TEXT },
  [EventGuest_Eligibility.ELIGIBILITY_REJECTED]: { tone: 'error', text: REJECTED_TEXT },
};

/**
 * A guest as the server sent them, refused here when their eligibility is
 * one this page does not know: the card is drawn from it, and a guess could
 * put an "Issue" button in front of someone the server would refuse.
 */
export function checkedGuest(guest: EventGuest): EventGuest {
  if (ELIGIBILITY_REFUSALS[guest.eligibility] === undefined) {
    throw new Error(`The server sent guest ${guest.userId} with an eligibility this page does not know (${String(guest.eligibility)})`);
  }
  return guest;
}

/** What the last tap of "Issue" came to, for the guest it was tapped for. */
export type IssueOutcome =
  | { readonly kind: 'issued' }
  | { readonly kind: 'already-issued' }
  | { readonly kind: 'refused'; readonly notice: Notice };

/**
 * What the card shows under the guest's name:
 *
 * - `issue`: the button, with what the last tap was refused for above it,
 *   if it was.
 * - `issued-now`: this tap handed them over.
 * - `issued-before`: they already have them; `repeat` when a tap just found
 *   that out, which is a warning, not a record.
 * - `refused`: their verification does not allow it, and there is no button.
 */
export type GuestCardView =
  | { readonly kind: 'issue'; readonly buttonLabel: string; readonly problem: Notice | null }
  | { readonly kind: 'issued-now'; readonly text: string; readonly detail: string }
  | { readonly kind: 'issued-before'; readonly text: string; readonly repeat: boolean }
  | { readonly kind: 'refused'; readonly notice: Notice };

export function guestCardView(guest: EventGuest, outcome: IssueOutcome | null, now: number): GuestCardView {
  // The gateway writes `null`, not nothing, for a guest not yet handed
  // tickets; a fixture may leave the field out.
  const issuance = guest.ticketIssuance;
  if (issuance !== undefined && issuance !== null) {
    const when = `${momentText(issuance.issueTime, now)} by ${operatorText(issuance.actor)}`;
    if (outcome?.kind === 'issued') {
      return { kind: 'issued-now', text: `Issued. Hand over ${ticketsText(issuance.ticketCount)}.`, detail: `Issued ${when}` };
    }
    if (outcome?.kind === 'already-issued') return { kind: 'issued-before', text: `Already issued ${when}.`, repeat: true };
    return { kind: 'issued-before', text: `Issued ${when}`, repeat: false };
  }

  const refusal = ELIGIBILITY_REFUSALS[guest.eligibility];
  if (refusal === undefined) throw new Error(`Event guest has an unknown eligibility ${String(guest.eligibility)}`);
  if (refusal !== null) return { kind: 'refused', notice: refusal };

  return {
    kind: 'issue',
    buttonLabel: `Issue ${ticketsText(guest.event.guestTicketCount)}`,
    problem: outcome?.kind === 'refused' ? outcome.notice : null,
  };
}

// -- A failed tap --

/**
 * partner_service's refusals of IssueEventTickets (common/go/aip/errors,
 * LIM-4704), passed through admin_api unchanged. Each carries `satisfies`, so
 * a reason renamed or dropped on the backend fails the typecheck here.
 */
export const EVENT_REFUSAL_REASON = {
  ALREADY_ISSUED: 'EVENT_TICKETS_ALREADY_ISSUED' satisfies LiminalErrorReason,
  NOT_SUBMITTED: 'EVENT_GUEST_NOT_SUBMITTED' satisfies LiminalErrorReason,
  REJECTED: 'EVENT_GUEST_REJECTED' satisfies LiminalErrorReason,
  CLOSED: 'EVENT_CLOSED' satisfies LiminalErrorReason,
  FULL: 'EVENT_FULL' satisfies LiminalErrorReason,
} as const;

export interface IssueFailure {
  readonly outcome: IssueOutcome;
  /**
   * Whether to read the guest again: the server says their tickets or their
   * verification are not what the card shows, so the card is redrawn from
   * what it holds now.
   */
  readonly reread: boolean;
}

function refused(tone: NoticeTone, text: string): IssueOutcome {
  return { kind: 'refused', notice: { tone, text } };
}

/** Outside the window: when it opens if that is still to come, else when it closed. */
function closedText(event: CheckInEvent, now: number): string {
  if (now >= parseTime(event.endTime)) return `Check-in closed at ${momentText(event.endTime, now)}.`;
  return `Check-in opens ${momentText(event.startTime, now)}.`;
}

/**
 * What a failed tap of "Issue" tells staff. Issuing is keyed on the event and
 * the guest, so a tap that never reached the server is safe to repeat, and
 * the copy says so.
 */
export function issueFailure(error: unknown, event: CheckInEvent, now: number): IssueFailure {
  const parsed = parseError(error);
  if (parsed.code === EVENT_REFUSAL_REASON.ALREADY_ISSUED) return { outcome: { kind: 'already-issued' }, reread: true };
  if (parsed.code === EVENT_REFUSAL_REASON.NOT_SUBMITTED) return { outcome: refused('warning', NOT_SUBMITTED_TEXT), reread: true };
  if (parsed.code === EVENT_REFUSAL_REASON.REJECTED) return { outcome: refused('error', REJECTED_TEXT), reread: true };
  if (parsed.code === EVENT_REFUSAL_REASON.CLOSED) return { outcome: refused('warning', closedText(event, now)), reread: false };
  if (parsed.code === EVENT_REFUSAL_REASON.FULL) return { outcome: refused('error', FULL_TEXT), reread: false };
  if (parsed.category === 'NETWORK' || parsed.category === 'TIMEOUT') {
    return { outcome: refused('error', TAP_AGAIN_TEXT), reread: false };
  }
  return { outcome: refused('error', getErrorMessage(error)), reread: false };
}

// -- A failed lookup --

/**
 * What the card's place shows when a scan or a search found no one. Asking
 * again cannot find a guest who is not there, or fix what was typed, so only
 * other failures offer it.
 */
export function lookupProblem(error: unknown): { readonly title: string; readonly message: string; readonly retry: boolean } {
  const { category } = parseError(error);
  if (category === 'NOT_FOUND') return { title: 'No guest found', message: getErrorMessage(error), retry: false };
  return { title: 'The guest did not load', message: getErrorMessage(error), retry: category !== 'VALIDATION' };
}
